import MotionController from "../lib/motion_controller"
import { gsap, ScrollTrigger } from "../lib/motion"

// "Your knowledge is everywhere": connector glyphs start scattered, settle into a ring as
// connection lines draw in, then get pulled into the central index node.
//
// Static markup is the "connected ring" moment. Each node carries its ring position
// (data-x / data-y, % of the field) and a scatter offset (data-sx / data-sy, % of the field).
// Desktop: scrubbed over the pinned range. Mobile: plays once when scrolled into view.
export default class extends MotionController {
  static targets = [ "field", "node", "line", "flow", "hub", "halo", "counter", "before", "after", "status" ]
  static values = {
    scrubTrigger: String,
    scrubEnd: { type: String, default: "+=150%" },
    total: Number
  }

  animate({ desktop, mobile }) {
    if (!desktop && !mobile) return

    const field = this.fieldTarget
    const width = () => field.offsetWidth
    const height = () => field.offsetHeight
    const pct = (node, key) => parseFloat(node.dataset[key]) || 0
    const original = this.hasCounterTarget ? this.counterTarget.textContent : ""
    const format = new Intl.NumberFormat("en-US")
    const counter = { value: 0 }
    const renderCount = () => {
      if (this.hasCounterTarget) this.counterTarget.textContent = format.format(Math.round(counter.value))
    }

    this.element.classList.add("is-animated")
    renderCount()

    const tl = gsap.timeline({ paused: mobile, defaults: { ease: "power2.inOut" } })

    tl.fromTo(this.nodeTargets, {
      x: (_, node) => pct(node, "sx") / 100 * width(),
      y: (_, node) => pct(node, "sy") / 100 * height(),
      rotation: (i) => (i % 2 ? 8 : -8),
      opacity: 0.35,
      scale: 0.88
    }, {
      x: 0, y: 0, rotation: 0, opacity: 1, scale: 1,
      duration: 4,
      stagger: { each: 0.07, from: "edges" }
    }, 0)

    tl.fromTo(this.lineTargets, { strokeDashoffset: 1 }, { strokeDashoffset: 0, duration: 2, stagger: 0.05 }, 2.4)
    tl.fromTo(this.flowTargets, { opacity: 0 }, { opacity: 1, duration: 1 }, 4)
    tl.fromTo(this.hubTargets, { scale: 0.86 }, { scale: 1, duration: 3, ease: "expo.out" }, 2.6)
    tl.fromTo(this.haloTargets, { opacity: 0.15, scale: 0.8 }, { opacity: 0.7, scale: 1, duration: 3 }, 2.6)

    if (desktop && this.hasBeforeTarget && this.hasAfterTarget) {
      tl.to(this.beforeTarget, { opacity: 0, y: -24, duration: 0.9, ease: "power2.in" }, 5)
      tl.fromTo(this.afterTarget, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 1.4, ease: "expo.out" }, 6)
    } else if (this.hasAfterTarget) {
      tl.fromTo(this.afterTarget, { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 1.4, ease: "expo.out" }, 6)
    }

    tl.to(this.nodeTargets, {
      x: (_, node) => (50 - pct(node, "x")) / 100 * width(),
      y: (_, node) => (50 - pct(node, "y")) / 100 * height(),
      scale: 0.2,
      opacity: 0,
      duration: 2.6,
      ease: "power3.in",
      stagger: { each: 0.05, from: "random" }
    }, 6)
    tl.to([ ...this.lineTargets, ...this.flowTargets ], { opacity: 0, duration: 2 }, 6.4)
    tl.to(this.haloTargets, { scale: 1.35, opacity: 1, duration: 2.5, ease: "expo.out" }, 7.6)
    tl.to(counter, { value: this.totalValue, duration: 3.4, ease: "power2.out", onUpdate: renderCount }, 6.2)
    if (this.hasStatusTarget) tl.fromTo(this.statusTarget, { autoAlpha: 0, y: 6 }, { autoAlpha: 1, y: 0, duration: 1 }, 8.6)

    if (desktop) {
      const trigger = this.hasScrubTriggerValue ? this.element.closest(this.scrubTriggerValue) : this.element
      ScrollTrigger.create({
        trigger: trigger || this.element,
        start: "top top",
        end: this.scrubEndValue,
        scrub: 0.8,
        animation: tl,
        invalidateOnRefresh: true
      })
    } else {
      tl.timeScale(3)
      ScrollTrigger.create({ trigger: field, start: "top 65%", once: true, onEnter: () => tl.play() })
    }

    return () => {
      this.element.classList.remove("is-animated")
      if (this.hasCounterTarget) this.counterTarget.textContent = original
    }
  }
}
